import { ImageLoadRequestDTO } from '../ports/types';

export interface LoadedRawImage {
  assetId: string;
  imageSource: HTMLImageElement | ImageBitmap;
  naturalWidth: number;
  naturalHeight: number;
  mimeType: string;
  byteSize: number;
  dispose: () => void;
}

export interface ImageDecoder {
  decode(src: string): Promise<{ image: HTMLImageElement | ImageBitmap; width: number; height: number }>;
}

export interface ImageLoaderOptions {
  decoder?: ImageDecoder;
  createObjectURL?: (blob: Blob) => string;
  revokeObjectURL?: (url: string) => void;
  maxFileSizeBytes?: number;
  maxDimensionPx?: number;
}

const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10.0 MB
const DEFAULT_MAX_DIMENSION = 4096; // 4096 x 4096 px

const ALLOWED_MIME_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/svg+xml',
]);

const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.svg'];

/**
 * 画像ローダー＆バリデーションコンポーネント (ImageLoaderComponent)
 * ローカル画像ファイルの受領、形式・サイズ上限（10MB）・解像度（4096px）検証、
 * 非同期デコード、およびオブジェクトURLメモリライフサイクル管理を担う。
 */
export class ImageLoader {
  private decoder: ImageDecoder;
  private createObjectURLFn: (blob: Blob) => string;
  private revokeObjectURLFn: (url: string) => void;
  private maxFileSizeBytes: number;
  private maxDimensionPx: number;

  private currentObjectUrl: string | null = null;

  constructor(options?: ImageLoaderOptions) {
    this.maxFileSizeBytes = options?.maxFileSizeBytes ?? DEFAULT_MAX_FILE_SIZE;
    this.maxDimensionPx = options?.maxDimensionPx ?? DEFAULT_MAX_DIMENSION;

    this.createObjectURLFn = options?.createObjectURL ?? ((blob: Blob) => {
      if (typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function') {
        return URL.createObjectURL(blob);
      }
      return '';
    });

    this.revokeObjectURLFn = options?.revokeObjectURL ?? ((url: string) => {
      if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore
        }
      }
    });

    this.decoder = options?.decoder ?? {
      decode: (src: string) => this.defaultDecode(src),
    };
  }

  private defaultDecode(src: string): Promise<{ image: HTMLImageElement; width: number; height: number }> {
    return new Promise((resolve, reject) => {
      if (typeof Image === 'undefined') {
        reject(new Error('[ImageLoader] Image constructor is not available in this environment.'));
        return;
      }

      const img = new Image();
      img.crossOrigin = 'anonymous';

      const cleanup = () => {
        img.onload = null;
        img.onerror = null;
      };

      img.onload = () => {
        cleanup();
        const width = img.naturalWidth || img.width;
        const height = img.naturalHeight || img.height;
        resolve({ image: img, width, height });
      };

      img.onerror = (e) => {
        cleanup();
        reject(new Error(`[ImageLoader] Failed to decode image: ${e}`));
      };

      img.src = src;

      // decode API が利用可能な場合は並行して呼び出し
      if (typeof img.decode === 'function') {
        img.decode().then(() => {
          // decode成功時
        }).catch(() => {
          // onloadでのフォールバックに任せる
        });
      }
    });
  }

  /**
   * 画像リクエストを受領し検証・デコード
   */
  public async load(request: ImageLoadRequestDTO): Promise<LoadedRawImage> {
    if (!request || !request.source) {
      throw new Error('[ImageLoader:INVALID_INPUT_SOURCE] 有効な画像ソースが指定されていません。');
    }

    let srcUrl = '';
    let mimeType = 'image/png';
    let byteSize = 0;
    let isCreatedUrl = false;

    if (typeof request.source === 'string') {
      srcUrl = request.source;
      if (srcUrl.startsWith('data:')) {
        const mimeMatch = srcUrl.match(/^data:([^;]+);/);
        if (mimeMatch) {
          mimeType = mimeMatch[1].toLowerCase();
        }
        // Base64のおおよそのバイトサイズ推定
        byteSize = Math.round((srcUrl.length * 3) / 4);
      }
    } else {
      const blob = request.source;
      byteSize = blob.size;

      // 1. ファイルサイズ上限チェック
      if (byteSize > this.maxFileSizeBytes) {
        throw new Error(
          `[ImageLoader:FILE_SIZE_EXCEEDED] ファイルサイズが上限（10MB）を超えています。(${(byteSize / (1024 * 1024)).toFixed(2)} MB)`
        );
      }

      // 2. MIMEタイプおよび拡張子チェック
      const validation = await this.validateBlob(blob);
      if (!validation.isValid) {
        throw new Error(`[ImageLoader:${validation.errorCode}] ${validation.errorMessage}`);
      }
      mimeType = validation.mimeType;

      // オブジェクトURL生成
      srcUrl = this.createObjectURLFn(blob);
      isCreatedUrl = true;
    }

    try {
      // 3. 非同期デコード
      const decoded = await this.decoder.decode(srcUrl);

      // 4. 解像度チェック
      if (decoded.width > this.maxDimensionPx || decoded.height > this.maxDimensionPx) {
        if (isCreatedUrl) this.revokeObjectURLFn(srcUrl);
        throw new Error(
          `[ImageLoader:DIMENSION_EXCEEDED] 画像の解像度が上限を超えています（${decoded.width}x${decoded.height} px, 最大: ${this.maxDimensionPx}x${this.maxDimensionPx} px）。`
        );
      }

      // 直前のオブジェクトURLを解放
      this.disposeCurrent();
      if (isCreatedUrl) {
        this.currentObjectUrl = srcUrl;
      }

      const assetId = `asset_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
      const activeUrl = isCreatedUrl ? srcUrl : null;

      return {
        assetId,
        imageSource: decoded.image,
        naturalWidth: decoded.width,
        naturalHeight: decoded.height,
        mimeType,
        byteSize,
        dispose: () => {
          if (activeUrl) {
            this.revokeObjectURLFn(activeUrl);
          }
        },
      };
    } catch (err) {
      if (isCreatedUrl) {
        this.revokeObjectURLFn(srcUrl);
      }
      throw err;
    }
  }

  /**
   * Blobの形式およびマジックナンバーの検証
   */
  public async validateBlob(blob: Blob): Promise<{ isValid: boolean; mimeType: string; errorCode?: string; errorMessage?: string }> {
    const rawType = (blob.type || '').toLowerCase();
    const fileName = (blob as File).name ? (blob as File).name.toLowerCase() : '';

    let matchedMime = rawType;

    // MIMEが空または不明な場合、拡張子から推測
    if (!matchedMime || matchedMime === 'application/octet-stream') {
      for (const ext of ALLOWED_EXTENSIONS) {
        if (fileName.endsWith(ext)) {
          if (ext === '.jpg' || ext === '.jpeg') matchedMime = 'image/jpeg';
          else if (ext === '.png') matchedMime = 'image/png';
          else if (ext === '.webp') matchedMime = 'image/webp';
          else if (ext === '.svg') matchedMime = 'image/svg+xml';
          break;
        }
      }
    }

    if (!ALLOWED_MIME_TYPES.has(matchedMime)) {
      return {
        isValid: false,
        mimeType: rawType,
        errorCode: 'UNSUPPORTED_FORMAT',
        errorMessage: '対応していない画像形式です。（JPEG, PNG, WebP, SVGに対応）',
      };
    }

    // マジックナンバー先頭バイト検査 (SVG以外)
    if (matchedMime !== 'image/svg+xml' && typeof blob.slice === 'function') {
      try {
        const headerBuffer = await blob.slice(0, 16).arrayBuffer();
        const bytes = new Uint8Array(headerBuffer);

        if (matchedMime === 'image/jpeg') {
          // JPEG: FF D8 FF
          if (bytes[0] !== 0xff || bytes[1] !== 0xd8 || bytes[2] !== 0xff) {
            return {
              isValid: false,
              mimeType: matchedMime,
              errorCode: 'CORRUPTED_FILE',
              errorMessage: 'JPEGヘッダーが不正または破損しています。',
            };
          }
        } else if (matchedMime === 'image/png') {
          // PNG: 89 50 4E 47
          if (bytes[0] !== 0x89 || bytes[1] !== 0x50 || bytes[2] !== 0x4e || bytes[3] !== 0x47) {
            return {
              isValid: false,
              mimeType: matchedMime,
              errorCode: 'CORRUPTED_FILE',
              errorMessage: 'PNGヘッダーが不正または破損しています。',
            };
          }
        } else if (matchedMime === 'image/webp') {
          // WebP: RIFF (52 49 46 46) ... WEBP (57 45 42 50)
          const isRIFF = bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46;
          const isWEBP = bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50;
          if (!isRIFF || !isWEBP) {
            return {
              isValid: false,
              mimeType: matchedMime,
              errorCode: 'CORRUPTED_FILE',
              errorMessage: 'WebPヘッダーが不正または破損しています。',
            };
          }
        }
      } catch {
        // slice / arrayBuffer が利用不可な場合はMIME信頼
      }
    }

    return { isValid: true, mimeType: matchedMime };
  }

  /**
   * 直前のオブジェクトURLを明示的に解放
   */
  public disposeCurrent(): void {
    if (this.currentObjectUrl) {
      this.revokeObjectURLFn(this.currentObjectUrl);
      this.currentObjectUrl = null;
    }
  }
}
